/**
 * Betrags-Bereichsfilter (Von/Bis) der Spaltenfilter (Auftrag "Bestellboard-Filter datentypgerecht
 * bauen", 2026-09-01) - NEUBAU ohne Bestandsvorbild. Ersetzt den bisherigen Exakttreffer auf
 * `"{Waehrung}:{Betrag}"` fuer die Betragsspalte (`value`, `lib/orderList.ts#ORDER_LIST_MONEY_KEYS`).
 *
 * Bei gemischten Waehrungen in der sichtbaren Liste zeigt das Widget EIN Von/Bis-Paar JE Waehrung
 * (`waehrungen`, aus `orderListMoneyWaehrungen` - keine Umrechnung, siehe Auftragsbrief). Das
 * Waehrungskuerzel steht sichtbar neben den Feldern, damit der Bezug eindeutig bleibt.
 *
 * NACHBESSERUNG W1/W2 (Pruefung): der Knopf "Nur ohne Wert" bildet den Zustand nach, den das alte
 * `<select>` ueber die Facettenoption "Ohne Wert" (`__EMPTY__`) anbot - waehrungsUNabhaengig
 * (`lib/orderList.ts#Bereichsfilter`, reserviertes Pseudo-Waehrungskuerzel `__EMPTY__`), deshalb
 * ausserhalb der Waehrungsschleife und UNABHAENGIG davon sichtbar, ob `waehrungen` leer ist - eine
 * Bestellung ohne Betrag hat trotzdem eine zugeordnete Waehrung (`orderCurrency`), zaehlt aber nicht
 * in `waehrungen` (siehe `orderListMoneyWaehrungen`). Aktiviert, sperrt er alle Von/Bis-Felder.
 */
import type { Bereichsfilter } from "../../../lib/orderList.ts";

/** Das reservierte, waehrungsunabhaengige Pseudo-Kuerzel fuer "nur ohne Wert" - siehe Kopfkommentar. */
const OHNE_WERT_SCHLUESSEL = "__EMPTY__";

export interface BetragsBereichFilterProps {
  label: string;
  waehrungen: readonly string[];
  bereiche: Readonly<Record<string, Bereichsfilter>>;
  onAendern: (waehrung: string, bereich: Bereichsfilter) => void;
}

export function BetragsBereichFilter({
  label,
  waehrungen,
  bereiche,
  onAendern,
}: BetragsBereichFilterProps) {
  const ohneWert = Boolean(bereiche[OHNE_WERT_SCHLUESSEL]?.ohneWert);
  return (
    <div className="spaltenfilter-geld">
      {waehrungen.length ? (
        waehrungen.map((waehrung) => {
          const bereich = bereiche[waehrung] ?? {};
          return (
            <div key={waehrung} className="spaltenfilter-geld-waehrung">
              <span className="spaltenfilter-geld-kuerzel">{waehrung}</span>
              <div className="spaltenfilter-bereich">
                <label className="spaltenfilter-bereich-feld">
                  <span>Von</span>
                  <input
                    type="number"
                    aria-label={`${label} ${waehrung} von`}
                    value={bereich.von ?? ""}
                    disabled={ohneWert}
                    onChange={(ereignis) =>
                      onAendern(waehrung, { ...bereich, von: ereignis.target.value })
                    }
                  />
                </label>
                <label className="spaltenfilter-bereich-feld">
                  <span>Bis</span>
                  <input
                    type="number"
                    aria-label={`${label} ${waehrung} bis`}
                    value={bereich.bis ?? ""}
                    disabled={ohneWert}
                    onChange={(ereignis) =>
                      onAendern(waehrung, { ...bereich, bis: ereignis.target.value })
                    }
                  />
                </label>
              </div>
            </div>
          );
        })
      ) : (
        <p className="spaltenfilter-keine-treffer">Keine Beträge in der aktuellen Liste.</p>
      )}
      <label className="spaltenfilter-option spaltenfilter-ohne-wert">
        <input
          type="checkbox"
          aria-label={`${label}: nur ohne Wert`}
          checked={ohneWert}
          onChange={(ereignis) =>
            onAendern(OHNE_WERT_SCHLUESSEL, {
              ...bereiche[OHNE_WERT_SCHLUESSEL],
              ohneWert: ereignis.target.checked,
            })
          }
        />
        <span>Nur ohne Wert</span>
      </label>
    </div>
  );
}
